import React, { useEffect, useRef, useState } from "react";
import { fmtMoney } from "@/lib/format";

/**
 * AnimatedNumber — eases a numeric value from 0 (or prevValue) to `value`
 * with a "premium" easeOutExpo curve. Falls back to no animation when the user
 * has `prefers-reduced-motion: reduce`.
 *
 * Default `format` is `fmtMoney` (Swiss accounting: 1'234,56) so KPI strips,
 * dashboards and Buchhaltung always show 2 decimals with apostrophe-thousands
 * and comma-decimal — matching the rest of the CRM.
 *
 * Props:
 *   value     number  (required)  — target value
 *   duration  number  (default 900ms)
 *   format    fn      (default fmtMoney — 1234.5 -> "1'234,50")
 *   className string
 */
const easeOutExpo = (t) => (t === 1 ? 1 : 1 - Math.pow(2, -10 * t));

export default function AnimatedNumber({
  value = 0,
  duration = 900,
  format = fmtMoney,
  className = "",
  testId,
}) {
  const [display, setDisplay] = useState(0);
  const fromRef = useRef(0);
  const startRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    // Respect reduced motion
    const reduce = typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduce) { setDisplay(value); return; }

    fromRef.current = display;
    startRef.current = null;

    const tick = (ts) => {
      if (startRef.current == null) startRef.current = ts;
      const t = Math.min(1, (ts - startRef.current) / duration);
      const eased = easeOutExpo(t);
      const next = fromRef.current + (value - fromRef.current) * eased;
      setDisplay(next);
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [value, duration]);

  return <span className={className} data-testid={testId}>{format(display)}</span>;
}
